import React from 'react';
import { createPortal } from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import Select from 'react-select';
import { TemplateIcon } from '../../svg/TemplateIcons';

const templateIconStyle = { color: "var(--details-icons-color, var(--sidenav-icons, #FFFFFF))" };

const TemplateExampleCopyPills = ({ labels }) => (
    <div className="td-example-modal__copy-list">
        {labels.map((label) => (
            <span key={label} className="td-example-modal__copy-pill">
                {label}
            </span>
        ))}
    </div>
);

const TemplateExampleDialogs = ({
    t,
    isRtl,
    markAsExampleDialog,
    closeMarkAsExampleDialog,
    handleMarkAsExampleNameChange,
    submitMarkAsExample,
    isMarkAsExampleSubmitDisabled,
    isExampleNameDuplicate,
    loadingTemplateExamples,
    attachExampleDialog,
    closeAttachExampleDialog,
    attachedTemplateExample,
    attachExampleOptions,
    selectedAttachExampleOption,
    handleAttachExampleSelection,
    submitAttachExample,
    templateExampleSelectStyles,
}) => (
    <>
        {typeof document !== 'undefined' && markAsExampleDialog.open
            ? createPortal(
                  <div
                      id="modal"
                      onMouseDown={() => {
                          if (!markAsExampleDialog.submitting) {
                              closeMarkAsExampleDialog();
                          }
                      }}
                  >
                      <div
                          id="popup"
                          className="dashboard-modal td-example-modal"
                          role="dialog"
                          aria-modal="true"
                          aria-labelledby="template-example-modal-title"
                          onMouseDown={(event) => event.stopPropagation()}
                      >
                          <div className="dashboard-header td-example-modal__header">
                              <div className="dashboard-header-icon">
                                  <TemplateIcon style={templateIconStyle} />
                              </div>

                              <div className="dashboard-header-text">
                                  <h2 id="template-example-modal-title">
                                      {t.markAsExample || 'Mark as Example'}
                                  </h2>
                                  <p>
                                      {t.markAsExampleDescription ||
                                          'Choose a unique example name and we will copy this template into the examples library.'}
                                  </p>
                              </div>

                              <button
                                  type="button"
                                  className="td-example-modal__close"
                                  onClick={closeMarkAsExampleDialog}
                                  disabled={markAsExampleDialog.submitting}
                                  aria-label={t.close || 'Close'}
                              >
                                  <CloseIcon fontSize="small" />
                              </button>
                          </div>

                          <div className="modal-divider" />

                          <div className="modal-content dashboard-content td-example-modal__content">
                              <div className="form-item">
                                  <label
                                      className="form-lbl"
                                      htmlFor="template-example-name"
                                      style={{ color: 'var(--header-color)', marginLeft: 0, marginRight: 0 }}
                                  >
                                      {t.exampleNameLabel || 'Example Name'}
                                  </label>
                                  <input
                                      id="template-example-name"
                                      type="text"
                                      className="add-column td-example-modal__input"
                                      placeholder={t.exampleNamePlaceholder || 'Enter a unique example name'}
                                      value={markAsExampleDialog.exampleName}
                                      onChange={handleMarkAsExampleNameChange}
                                      onKeyDown={(event) => {
                                          if (event.key === 'Enter') {
                                              event.preventDefault();
                                              if (!isMarkAsExampleSubmitDisabled) {
                                                  submitMarkAsExample();
                                              }
                                          }
                                      }}
                                      autoFocus
                                      maxLength={100}
                                      disabled={markAsExampleDialog.submitting}
                                  />
                                  <div className="td-example-modal__helper">
                                      {loadingTemplateExamples
                                          ? (t.markAsExampleChecking || 'Checking existing examples...')
                                          : (t.markAsExampleHelper || 'This name must be unique across all template examples.')}
                                  </div>
                                  {isExampleNameDuplicate && !markAsExampleDialog.error ? (
                                      <div className="td-example-modal__error">
                                          {t.markAsExampleUnique || 'Example name must be unique.'}
                                      </div>
                                  ) : null}
                                  {markAsExampleDialog.error ? (
                                      <div className="td-example-modal__error">
                                          {markAsExampleDialog.error}
                                      </div>
                                  ) : null}
                              </div>

                              <div className="td-example-modal__copy-card">
                                  <div className="td-example-modal__copy-title">
                                      {t.markAsExampleCopiedFields || 'Copied from this template'}
                                  </div>
                                  <TemplateExampleCopyPills
                                      labels={[
                                          t.exampleCopyName || 'Name',
                                          t.exampleCopyDatastreams || 'Datastreams',
                                          t.exampleCopyWebDashboard || 'Web Dashboard',
                                          t.exampleCopyMobileDashboard || 'Mobile Dashboard',
                                          t.exampleCopyEvents || 'Events',
                                      ]}
                                  />
                              </div>
                          </div>

                          <div className="modal-divider" />

                          <div className="modal-footer dashboard-footer">
                              <button
                                  className="btn btn-cancel-details"
                                  onClick={closeMarkAsExampleDialog}
                                  disabled={markAsExampleDialog.submitting}
                              >
                                  {t.cancel || 'Cancel'}
                              </button>

                              <div className="modal-footer-right-buttons">
                                  <button
                                      className="btn btn-apply"
                                      onClick={submitMarkAsExample}
                                      disabled={isMarkAsExampleSubmitDisabled}
                                  >
                                      {markAsExampleDialog.submitting
                                          ? (t.markAsExampleSaving || 'Saving...')
                                          : (t.markAsExampleSave || 'Save as Example')}
                                  </button>
                              </div>
                          </div>
                      </div>
                  </div>,
                  document.body
              )
            : null}

        {typeof document !== 'undefined' && attachExampleDialog.open
            ? createPortal(
                  <div
                      id="modal"
                      onMouseDown={() => {
                          if (!attachExampleDialog.submitting) {
                              closeAttachExampleDialog();
                          }
                      }}
                  >
                      <div
                          id="popup"
                          className="dashboard-modal td-example-modal"
                          role="dialog"
                          aria-modal="true"
                          aria-labelledby="template-attach-example-modal-title"
                          onMouseDown={(event) => event.stopPropagation()}
                      >
                          <div className="dashboard-header td-example-modal__header">
                              <div className="dashboard-header-icon">
                                  <TemplateIcon style={templateIconStyle} />
                              </div>

                              <div className="dashboard-header-text">
                                  <h2 id="template-attach-example-modal-title">
                                      {attachedTemplateExample
                                          ? t.changeAttachedExample || 'Change Attached Example'
                                          : t.attachToExample || 'Attach to Example'}
                                  </h2>
                                  <p>
                                      {t.attachExampleDescription ||
                                          'Select a template example. Its shared fields will be copied into this template while keeping the current template name and template ID.'}
                                  </p>
                              </div>

                              <button
                                  type="button"
                                  className="td-example-modal__close"
                                  onClick={closeAttachExampleDialog}
                                  disabled={attachExampleDialog.submitting}
                                  aria-label={t.close || 'Close'}
                              >
                                  <CloseIcon fontSize="small" />
                              </button>
                          </div>

                          <div className="modal-divider" />

                          <div className="modal-content dashboard-content td-example-modal__content">
                              <div className="form-item">
                                  <label
                                      className="form-lbl"
                                      htmlFor="template-attach-example"
                                      style={{ color: 'var(--header-color)', marginLeft: 0, marginRight: 0 }}
                                  >
                                      {t.attachExampleSelectLabel || t.exampleNameLabel || 'Template Example'}
                                  </label>
                                  <Select
                                      inputId="template-attach-example"
                                      isRtl={isRtl}
                                      classNamePrefix="react-select"
                                      options={attachExampleOptions}
                                      value={selectedAttachExampleOption}
                                      onChange={handleAttachExampleSelection}
                                      placeholder={
                                          loadingTemplateExamples
                                              ? t.markAsExampleChecking || 'Loading examples...'
                                              : t.attachExamplePlaceholder || 'Select a template example'
                                      }
                                      isLoading={loadingTemplateExamples}
                                      isDisabled={attachExampleDialog.submitting}
                                      styles={templateExampleSelectStyles}
                                  />
                                  <div className="td-example-modal__helper">
                                      {t.attachExampleHelper ||
                                          'The selected example will sync description, hardware, connection type, hotspot prefix, datastreams, dashboards, events, and icon.'}
                                  </div>
                                  {attachExampleDialog.error ? (
                                      <div className="td-example-modal__error">
                                          {attachExampleDialog.error}
                                      </div>
                                  ) : null}
                              </div>

                              <div className="td-example-modal__copy-card">
                                  <div className="td-example-modal__copy-title">
                                      {t.attachExampleOverwriteTitle || 'Will sync from the selected example'}
                                  </div>
                                  <TemplateExampleCopyPills
                                      labels={[
                                          t.exampleCopyDatastreams || 'Datastreams',
                                          t.exampleCopyWebDashboard || 'Web Dashboard',
                                          t.exampleCopyMobileDashboard || 'Mobile Dashboard',
                                          t.exampleCopyEvents || 'Events',
                                          t.TemplateSettings?.descriptionLabel || 'Description',
                                          t.TemplateSettings?.hardwareLabel || 'Hardware',
                                          t.TemplateSettings?.connectionTypeLabel || 'Connection Type',
                                          t.TemplateSettings?.hotspotPrefixLabel || 'Hotspot Prefix',
                                          t.TemplateSettings?.iconLabel || 'Icon',
                                      ]}
                                  />
                              </div>

                              <div className="td-example-modal__copy-card">
                                  <div className="td-example-modal__copy-title">
                                      {t.attachExampleKeepsTitle || 'Will keep this template specific'}
                                  </div>
                                  <TemplateExampleCopyPills
                                      labels={[
                                          t.TemplateSettings?.nameLabel || 'Name',
                                          t.TemplateSettings?.templateIdLabel || 'Template ID',
                                      ]}
                                  />
                              </div>
                          </div>

                          <div className="modal-divider" />

                          <div className="modal-footer dashboard-footer">
                              <button
                                  className="btn btn-cancel-details"
                                  onClick={closeAttachExampleDialog}
                                  disabled={attachExampleDialog.submitting}
                              >
                                  {t.cancel || 'Cancel'}
                              </button>

                              <div className="modal-footer-right-buttons">
                                  <button
                                      className="btn btn-apply"
                                      onClick={submitAttachExample}
                                      disabled={
                                          attachExampleDialog.submitting ||
                                          !attachExampleDialog.selectedExampleId
                                      }
                                  >
                                      {attachExampleDialog.submitting
                                          ? t.attachExampleSaving || 'Syncing...'
                                          : attachedTemplateExample
                                          ? t.changeAttachedExample || 'Change Example'
                                          : t.attachToExample || 'Attach to Example'}
                                  </button>
                              </div>
                          </div>
                      </div>
                  </div>,
                  document.body
              )
            : null}
    </>
);

export default TemplateExampleDialogs;
